// src/components/SystemRecommendation/components/NextSteps.tsx

import React from 'react';
import { ViewMode, PricingInfo } from '../types';

interface NextStepsProps {
  viewMode: ViewMode;
  price?: PricingInfo;
}

const NextSteps: React.FC<NextStepsProps> = ({ viewMode, price }) => {
  if (viewMode === 'customer') {
    return (
      <div className="next-steps customer">
        {price && (
          <div className="pricing-section">
            <h2 className="section-title">Your Investment</h2>

            <div className="price-range">
              <p className="price-label">Estimated system cost:</p>
              <p className="price-value">
                £{price.rangeMin.toLocaleString()} - £{price.rangeMax.toLocaleString()}
              </p>
            </div>

            <div className="includes-section">
              <h3>This includes:</h3>
              <ul className="includes-list">
                {price.includes.map((item, index) => (
                  <li key={index}>• {item}</li>
                ))}
              </ul>
            </div>

            {price.financeAvailable && price.financeMonthly && (
              <div className="finance-section">
                <p className="finance-available">
                  Finance available from £{price.financeMonthly}/month*
                </p>
                <p className="finance-terms">
                  * Subject to credit approval. Representative APR {price.financeAPR}%
                </p>
              </div>
            )}
          </div>
        )}

        <div className="next-steps-section">
          <h2 className="section-title">What Happens Next?</h2>

          <div className="steps-timeline">
            <div className="step-item">
              <div className="step-number">1️⃣</div>
              <div className="step-content">
                <h3>Take time to think about it</h3>
                <p>No pressure - this is a big decision</p>
              </div>
            </div>

            <div className="step-item">
              <div className="step-number">2️⃣</div>
              <div className="step-content">
                <h3>Ask us any questions</h3>
                <p>We're here to help you understand</p>
              </div>
            </div>

            <div className="step-item">
              <div className="step-number">3️⃣</div>
              <div className="step-content">
                <h3>If you'd like to go ahead:</h3>
                <ul>
                  <li>We'll book a full survey</li>
                  <li>Confirm everything in person</li>
                  <li>Give you a final written quote</li>
                </ul>
              </div>
            </div>

            <div className="step-item">
              <div className="step-number">4️⃣</div>
              <div className="step-content">
                <h3>You'll have 14 days to change your mind</h3>
                <p>(cooling-off period)</p>
              </div>
            </div>
          </div>

          <div className="validity-notice">
            <p>This recommendation is valid for 30 days from today.</p>
          </div>
        </div>

        <div className="consumer-rights">
          <h2 className="section-title">Your Rights</h2>

          <div className="rights-grid">
            <div className="right-item">
              <h4>14-Day Cooling-Off Period</h4>
              <p>You can cancel for any reason within 14 days of signing</p>
            </div>

            <div className="right-item">
              <h4>Cancellation Policy</h4>
              <p>Full refund if cancelled before work begins</p>
            </div>

            <div className="right-item">
              <h4>Complaints Procedure</h4>
              <p>Contact us at complaints@britishgas.co.uk</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Technical mode
  return (
    <div className="next-steps technical">
      <h2 className="section-title">Next Steps</h2>

      <div className="technical-next-steps">
        <div className="verification-required">
          <h3>Verification Required Before Quote:</h3>
          <ul className="verification-checklist">
            <li className="checklist-item">
              <input type="checkbox" id="heat-loss" />
              <label htmlFor="heat-loss">Full heat loss survey</label>
            </li>
            <li className="checklist-item">
              <input type="checkbox" id="radiator-audit" />
              <label htmlFor="radiator-audit">Radiator audit & sizing confirmation</label>
            </li>
            <li className="checklist-item">
              <input type="checkbox" id="cylinder-space" />
              <label htmlFor="cylinder-space">Cylinder space measurement</label>
            </li>
            <li className="checklist-item">
              <input type="checkbox" id="gas-pressure" />
              <label htmlFor="gas-pressure">Gas pressure test (day of install)</label>
            </li>
            <li className="checklist-item">
              <input type="checkbox" id="pipework" />
              <label htmlFor="pipework">Pipework condition inspection</label>
            </li>
          </ul>
        </div>

        <div className="sign-offs-needed">
          <h3>Sign-Offs Needed:</h3>
          <ul className="sign-off-list">
            <li>Customer acceptance of recommendation</li>
            <li>Building Control notification (if required)</li>
            <li>Unvented cylinder G3 certification check</li>
            <li>Final quote approval</li>
          </ul>
        </div>

        <div className="compliance-notes">
          <h3>Compliance Notes:</h3>
          <ul className="compliance-list">
            <li>Part L Building Regulations applicable</li>
            <li>Boiler Plus requirements: Weather compensation mandatory</li>
            <li>Unvented cylinder: G3 qualified installer required</li>
            <li>Gas Safe registration: Required for all gas work</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default NextSteps;
